import React, { useMemo, useState } from 'react';
import { Button } from 'antd';
import StepIndicator from '../layout/StepsIndicator';
import ProtocolBasicInfoStep from './steps/ProtocolBasicInfoStep';
import ProtocolActivitiesStep from './steps/ProtocolActivitiesStep';
import ProtocolReviewStep from './steps/ProtocolReviewStep';
import {
  EMPTY_PROTOCOL_FORM,
  validateBasicInfo,
  validateActivities,
} from '../../utils/protocol-form.util';
import { useSubcategoryLookup } from '../../hooks/useSubcategoryLookup';

// `initialForm` must already be resolved (non-null) by the time this mounts
// for mode="edit" — the parent page keeps its own loading gate before
// rendering the wizard, same as it did before the wizard existed.
export default function ProtocolFormWizard({
  mode,
  initialForm,
  saving,
  onSubmit,
}) {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState(initialForm || EMPTY_PROTOCOL_FORM);
  // Every subcategory the pickers have loaded, plus the protocol's own tags.
  const { known, register } = useSubcategoryLookup(formData.subcategories);
  const subcategoryOptions = useMemo(() => Object.values(known), [known]);

  const steps = [
    {
      component: (
        <ProtocolBasicInfoStep
          mode={mode}
          formData={formData}
          setFormData={setFormData}
          subcategoryOptions={subcategoryOptions}
          onSubcategoriesLoaded={register}
        />
      ),
    },
    {
      component: <ProtocolActivitiesStep formData={formData} setFormData={setFormData} />,
    },
    {
      component: (
        <ProtocolReviewStep
          formData={formData}
          subcategoryOptions={subcategoryOptions}
        />
      ),
    },
  ];

  const next = () => {
    if (currentStep === 0 && !validateBasicInfo(formData, mode)) return;
    if (currentStep === 1 && !validateActivities(formData)) return;
    setCurrentStep((prev) => prev + 1);
  };

  const prev = () => setCurrentStep((prev) => prev - 1);

  const handleSubmit = () => {
    if (!validateBasicInfo(formData, mode) || !validateActivities(formData)) return;
    onSubmit(formData);
  };

  return (
    <div>
      <StepIndicator currentStep={currentStep} totalSteps={steps.length} />

      <div style={{ marginTop: 24 }}>{steps[currentStep].component}</div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 24 }}>
        {currentStep > 0 && (
          <Button className="btn-tertiary" onClick={prev} disabled={saving}>
            Nazad
          </Button>
        )}
        {currentStep < steps.length - 1 && (
          <Button className="btn-primary" type="primary" onClick={next}>
            Nastavi
          </Button>
        )}
        {currentStep === steps.length - 1 && (
          <Button className="btn-primary" type="primary" onClick={handleSubmit} loading={saving}>
            {mode === 'create' ? 'Kreiraj protokol' : 'Sačuvaj izmene'}
          </Button>
        )}
      </div>
    </div>
  );
}
